<template>
  <div class="purchase-page-wrapper">
    <div class="purchase-page-container">
      <!-- MAIN 2-COLUMN WORKSPACE -->
      <v-row class="fill-height ma-0" no-gutters>
        <!-- LEFT PANEL: Product Catalog with Category Filter -->
        <v-col cols="12" md="4" lg="3" class="d-flex flex-column pr-md-3 pb-3 pb-md-0 fill-height">
          <v-card
            class="ap-card product-card flex-grow-1 d-flex flex-column fill-height"
            outlined
            elevation="0"
          >
            <v-card-title class="py-3 px-4 d-flex align-center justify-space-between border-bottom flex-shrink-0 ap-card-title">
              <div class="d-flex align-center" style="gap: 10px;">
                <span class="ap-card-accent"></span>
                <span class="font-weight-bold text-subtitle-2">{{ $t("product_item") }}</span>
              </div>
              <v-chip x-small color="primary" outlined class="font-weight-bold">
                {{ filtered_products.length }} {{ $t("items") }}
              </v-chip>
            </v-card-title>

            <v-card-text class="pt-2 px-3 pb-2 d-flex flex-column flex-grow-1 overflow-hidden" style="min-height: 0;">
              <!-- Search & Filters Section -->
              <div class="filter-controls mb-2 flex-shrink-0">
                <div class="d-flex align-center" style="gap: 8px;">
                  <div class="flex-grow-1 min-w-0">
                    <TextSearch
                      v-model="search"
                      @search="fetchItem()"
                      @onClear="
                        search = '';
                        fetchItem();
                      "
                    />
                  </div>

                  <!-- Status Filter Dropdown Menu -->
                  <v-menu
                    offset-y
                    bottom
                    right
                    transition="slide-y-transition"
                    rounded="lg"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        outlined
                        height="40"
                        min-width="40"
                        class="px-2 rounded-lg filter-status-btn flex-shrink-0"
                        :color="stock_status_filter !== 'all' ? 'primary' : ''"
                        v-bind="attrs"
                        v-on="on"
                        :title="$t('status')"
                      >
                        <v-badge
                          dot
                          :value="stock_status_filter !== 'all'"
                          color="error"
                          offset-x="2"
                          offset-y="2"
                        >
                          <v-icon small :color="stock_status_filter !== 'all' ? 'primary' : 'grey darken-1'">
                            fal fa-filter
                          </v-icon>
                        </v-badge>
                        <v-icon x-small class="ml-1" :color="stock_status_filter !== 'all' ? 'primary' : 'grey darken-1'">
                          fal fa-chevron-down
                        </v-icon>
                      </v-btn>
                    </template>
                    <v-list dense class="py-1 status-filter-menu">
                      <v-list-item
                        v-for="status in stockStatusOptions"
                        :key="status.value"
                        @click="stock_status_filter = status.value"
                        :class="{ 'v-list-item--active primary--text': stock_status_filter === status.value }"
                      >
                        <v-list-item-icon class="mr-2 my-auto">
                          <v-icon small :color="status.color">{{ status.icon }}</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title class="text-caption font-weight-medium">
                            {{ status.text }}
                          </v-list-item-title>
                        </v-list-item-content>
                        <v-list-item-action v-if="stock_status_filter === status.value" class="my-auto ml-2">
                          <v-icon x-small color="primary">fal fa-check</v-icon>
                        </v-list-item-action>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </div>

                <!-- Category Horizontal Chips (Single Category Filter) -->
                <div v-if="categories && categories.length > 1" class="category-chips-wrapper mt-2">
                  <v-chip-group
                    v-model="selectedCategoryIndex"
                    mandatory
                    show-arrows
                    class="py-0"
                    active-class="active-category-chip"
                  >
                    <v-chip
                      v-for="(cat, idx) in categories"
                      :key="idx"
                      small
                      :color="selectedCategoryIndex === idx ? 'primary' : ''"
                      :text-color="selectedCategoryIndex === idx ? 'white' : ''"
                      :outlined="selectedCategoryIndex !== idx"
                      class="font-weight-medium text-caption"
                      :class="{ 'active-category-chip white--text': selectedCategoryIndex === idx }"
                      @click="onSelectCategoryChip(cat)"
                    >
                      {{ cat.category_display }}
                    </v-chip>
                  </v-chip-group>
                </div>
              </div>

              <!-- Product List Container -->
              <div class="product-list-container flex-grow-1 overflow-y-auto pr-1">
                <div
                  v-for="(item, index) in filtered_products"
                  :key="item.id || index"
                  class="product-item-card mb-2"
                  :class="{
                    'is-in-cart': getCartQty(item) > 0,
                    'is-loading': loadingProductId === item.id,
                    'is-disabled': overlay || (loadingProductId && loadingProductId !== item.id)
                  }"
                  @click="handleProductClick(item)"
                  :title="
                    item.has_variations
                      ? $t('click_to_select_variants')
                      : $t('click_to_add_product')
                  "
                >
                  <!-- Left Area: Thumb + Info -->
                  <div class="product-card-left">
                    <!-- Thumbnail with In-Cart Badge -->
                    <div class="product-thumb-box">
                      <app-img
                        :img="item.img"
                        width="46"
                        height="46"
                        class="product-thumb-img"
                      />
                      <span
                        v-if="getCartQty(item) > 0"
                        class="cart-count-badge"
                      >
                        {{ getCartQty(item) }}
                      </span>
                    </div>

                    <!-- Meta / Details -->
                    <div class="product-info-box">
                      <div class="product-name-text text-truncate">
                        {{ getProductName(item) }}
                      </div>

                      <div class="product-sub-row">
                        <span class="product-code-mono">{{ item.code }}</span>
                        <span class="product-dot-sep">•</span>
                        <!-- Stock Status -->
                        <span v-if="item.stock <= 0" class="stock-pill stock-pill-out">
                          {{ $t("out_of_stock") || "ໝົດສະຕັອກ" }} (0)
                        </span>
                        <span v-else-if="item.stock <= 10" class="stock-pill stock-pill-low">
                          {{ $t("low_stock") || "ໃກ້ໝົດ" }} ({{ item.stock }})
                        </span>
                        <span v-else class="stock-pill-normal">
                          {{ $t("stock") || "ສະຕັອກ" }}: {{ item.stock }}
                        </span>
                      </div>

                      <div class="product-price-row">
                        <span class="buy-price-val">
                          {{ priceWithCurrency(item.price_buy, item.currency) }}
                        </span>
                        <span
                          v-if="item.price_sale && !item.has_variations"
                          class="sale-price-val text-decoration-line-through"
                        >
                          {{ priceWithCurrency(item.price_sale, item.currency) }}
                        </span>
                        <span
                          v-if="item.has_variations"
                          class="variation-tag"
                        >
                          {{ $t("has_variations") || "Variations" }}
                        </span>
                      </div>
                    </div>
                  </div>

                  <!-- Right Area: Add Button / Inline Loader -->
                  <button
                    type="button"
                    class="product-add-btn"
                    :class="{
                      'btn-in-cart': getCartQty(item) > 0,
                      'btn-is-loading': loadingProductId === item.id
                    }"
                    @click.stop="handleProductClick(item)"
                    :title="$t('add')"
                  >
                    <v-progress-circular
                      v-if="loadingProductId === item.id"
                      indeterminate
                      size="16"
                      width="2"
                      color="primary"
                    />
                    <v-icon v-else small class="add-btn-icon">fal fa-plus</v-icon>
                  </button>
                </div>

                <!-- Empty State -->
                <div
                  v-if="!filtered_products || filtered_products.length === 0"
                  class="text-center py-8"
                >
                  <v-icon size="48" color="grey lighten-1">fal fa-box-open</v-icon>
                  <div class="mt-2 text-caption grey--text">
                    {{ $t("no_products_found") }}
                  </div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- RIGHT PANEL: Purchase Cart Table & HUD -->
        <v-col cols="12" md="8" lg="9" class="d-flex flex-column pl-md-0">
          <v-card
            class="ap-card purchase-main-card flex-grow-1 d-flex flex-column fill-height"
            outlined
            elevation="0"
          >
            <!-- Cart Card Header -->
            <v-card-title class="py-3 px-4 border-bottom d-flex align-center justify-space-between flex-shrink-0 ap-card-title">
              <div class="d-flex align-center flex-wrap" style="gap: 10px;">
                <span class="ap-card-accent"></span>
                <span class="font-weight-bold text-subtitle-2">{{ $t("purchased_items") || "ລາຍການສັ່ງຊື້" }}</span>
                <span class="ap-card-subtitle d-none d-sm-inline">({{ $t("check_and_edit_stock") || "ປັບປ່ຽນຈຳນວນ ຫຼື ລາຄາຕົ້ນທຶນໄດ້ໂດຍກົງ" }})</span>
              </div>

              <div class="d-flex align-center flex-wrap" style="gap: 10px;">
                <v-btn
                  v-if="desserts.length > 0"
                  text
                  small
                  color="error"
                  class="font-weight-bold text-caption rounded-lg"
                  @click="clearSelected"
                >
                  <v-icon left small>fal fa-trash</v-icon>
                  {{ $t("clear_selected") }}
                </v-btn>

                <v-btn
                  outlined
                  small
                  rounded
                  color="primary"
                  class="text-none font-weight-medium"
                  to="/report-purchase"
                >
                  <v-icon left x-small>fal fa-list</v-icon>
                  {{ $t("view_purchase") }}
                </v-btn>
              </div>
            </v-card-title>

            <!-- Scrollable Content Area -->
            <div class="cart-content-area flex-grow-1 overflow-auto">
              <v-data-table
                :headers="headers"
                :items="desserts"
                item-key="uniqueKey"
                hide-default-footer
                class="purchase-table elevation-0"
                :loading="overlay"
                loading-text="Loading products..."
                fixed-header
                :items-per-page="50"
              >
                <!-- Row Index -->
                <template slot="item.index" slot-scope="{ index }">
                  <span class="ps-row-index">{{ index + 1 }}</span>
                </template>

                <!-- Product Name & Thumbnail (with Code underneath) -->
                <template slot="item.name" slot-scope="{ item }">
                  <div class="d-flex align-center py-1">
                    <span class="ps-row-thumb mr-3 shrink-0">
                      <v-avatar tile size="36" class="rounded-lg ps-thumb-avatar">
                        <app-img :img="item.img" />
                      </v-avatar>
                    </span>
                    <div class="ps-row-name min-w-0">
                      <span class="ps-product-name text-truncate" :title="getProductName(item)">
                        {{ getProductName(item) }}
                      </span>
                      <div class="d-flex align-center flex-wrap" style="gap: 6px;">
                        <span v-if="item.code" class="ps-product-code">
                          {{ item.code }}
                          <button
                            type="button"
                            class="ps-copy-code"
                            :aria-label="$t('copy')"
                            :title="$t('copy')"
                            @click.stop="copyBarcode(item.code)"
                          >
                            <v-icon x-small>fal fa-copy</v-icon>
                          </button>
                        </span>
                        <!-- Variant chips if any -->
                        <template v-if="item.has_variations && item.selectedVariationOptions">
                          <span
                            v-for="(value, option) in item.selectedVariationOptions"
                            :key="option"
                            class="ps-category-pill"
                            style="font-size: 10px; padding: 1px 6px;"
                          >
                            {{ option }}: {{ value }}
                          </span>
                        </template>
                      </div>
                    </div>
                  </div>
                </template>

                <!-- Cost Price Inline Editable -->
                <template slot="item.price_buy" slot-scope="{ item }">
                  <div class="d-flex align-center justify-end">
                    <div class="ps-cost-input-box">
                      <input
                        type="number"
                        v-model.number="item.price_buy"
                        class="ps-cost-input"
                      />
                    </div>
                  </div>
                </template>

                <!-- Qty Stepper -->
                <template slot="item.qty" slot-scope="{ item }">
                  <div class="d-flex align-center justify-center">
                    <div class="ps-qty-stepper">
                      <button
                        type="button"
                        class="ps-qty-btn"
                        @click="decrement(item)"
                        :title="$t('decrement')"
                      >
                        <v-icon x-small>fal fa-minus</v-icon>
                      </button>
                      <input
                        type="number"
                        v-model.number="item.qty"
                        class="ps-qty-input"
                      />
                      <button
                        type="button"
                        class="ps-qty-btn ps-qty-btn--plus"
                        @click="increment(item)"
                        :title="$t('increment')"
                      >
                        <v-icon x-small>fal fa-plus</v-icon>
                      </button>
                    </div>
                  </div>
                </template>

                <!-- Sale Price -->
                <template slot="item.price_sale" slot-scope="{ item }">
                  <span class="ps-price-value text-right d-block">
                    {{ priceWithCurrency(item.price_sale, item.currency) }}
                  </span>
                </template>

                <!-- Margin Badge -->
                <template slot="item.margin" slot-scope="{ item }">
                  <div class="d-flex justify-center">
                    <span
                      class="ps-margin-pill"
                      :class="getItemMargin(item) >= 0 ? 'ps-margin-pill--pos' : 'ps-margin-pill--neg'"
                    >
                      {{ getItemMargin(item) >= 0 ? '+' : '' }}{{ getItemMargin(item) }}%
                    </span>
                  </div>
                </template>

                <!-- Total Cost Subtotal -->
                <template slot="item.total" slot-scope="{ item }">
                  <span class="ps-total-value text-right d-block">
                    {{ priceWithCurrency(item.price_buy * item.qty, item.currency) }}
                  </span>
                </template>

                <!-- Actions -->
                <template slot="item.actions" slot-scope="{ item, index }">
                  <div class="ps-row-actions justify-center">
                    <button
                      type="button"
                      class="ps-act"
                      :title="$t('view')"
                      @click="viewItem(item)"
                    >
                      <v-icon x-small>fal fa-eye</v-icon>
                    </button>
                    <button
                      type="button"
                      class="ps-act"
                      :title="$t('edit')"
                      @click="editItem(item)"
                    >
                      <v-icon x-small>fal fa-edit</v-icon>
                    </button>
                    <button
                      type="button"
                      class="ps-act ps-act--danger"
                      :title="$t('delete')"
                      @click="deleteItem(index)"
                    >
                      <v-icon x-small>fal fa-trash</v-icon>
                    </button>
                  </div>
                </template>

                <!-- Empty State Slot -->
                <template slot="no-data">
                  <div class="ps-empty text-center">
                    <v-icon size="48" class="ps-empty-icon mb-2">fal fa-shopping-basket</v-icon>
                    <div class="ps-empty-text font-weight-bold">
                      {{ $t("no_items_in_cart") || "ຍັງບໍ່ມີລາຍການສັ່ງຊື້" }}
                    </div>
                    <div class="ps-empty-subtext mt-1 text-caption">
                      {{ $t("select_products_from_left") || "ກະລຸນາເລືອກສິນຄ້າຈາກລາຍການດ້ານຊ້າຍເພື່ອເພີ່ມເຂົ້າໃບສັ່ງຊື້" }}
                    </div>
                  </div>
                </template>
              </v-data-table>
            </div>

            <!-- Bottom HUD (Financial & Margin Intelligence) -->
            <div class="cart-summary-area pa-3 border-top shrink-0">
              <v-row dense align="center" justify="space-between">
                
                <!-- Metrics Cards Grid (PosSmart Branding Style) -->
                <v-col cols="12" lg="8">
                  <v-row dense>
                    <!-- Metric 1: Items & Units -->
                    <v-col cols="6" sm="3">
                      <div class="ps-summary-card">
                        <div class="ps-summary-icon ps-summary-icon--primary">
                          <v-icon small>fal fa-boxes</v-icon>
                        </div>
                        <div class="ps-summary-meta">
                          <div class="ps-summary-label">{{ $t("items") }} / {{ $t("units") || "ຈຳນວນ" }}</div>
                          <div class="ps-summary-value text-truncate">
                            {{ desserts.length }} <span class="ps-summary-subval">({{ total_units }})</span>
                          </div>
                        </div>
                      </div>
                    </v-col>

                    <!-- Metric 2: Total Cost -->
                    <v-col cols="6" sm="3">
                      <div class="ps-summary-card">
                        <div class="ps-summary-icon ps-summary-icon--danger">
                          <v-icon small>fal fa-arrow-down</v-icon>
                        </div>
                        <div class="ps-summary-meta">
                          <div class="ps-summary-label">{{ $t("price_buy") }}</div>
                          <div class="ps-summary-value ps-summary-value--danger text-truncate">
                            {{ total_summary.price_buy | NumberFormat }} ₭
                          </div>
                        </div>
                      </div>
                    </v-col>

                    <!-- Metric 3: Projected Sales -->
                    <v-col cols="6" sm="3">
                      <div class="ps-summary-card">
                        <div class="ps-summary-icon ps-summary-icon--info">
                          <v-icon small>fal fa-arrow-up</v-icon>
                        </div>
                        <div class="ps-summary-meta">
                          <div class="ps-summary-label">{{ $t("price_sale") }}</div>
                          <div class="ps-summary-value ps-summary-value--info text-truncate">
                            {{ total_summary.price_sale | NumberFormat }} ₭
                          </div>
                        </div>
                      </div>
                    </v-col>

                    <!-- Metric 4: Profit & Margin -->
                    <v-col cols="6" sm="3">
                      <div class="ps-summary-card">
                        <div class="ps-summary-icon ps-summary-icon--success">
                          <v-icon small>fal fa-chart-line</v-icon>
                        </div>
                        <div class="ps-summary-meta">
                          <div class="d-flex align-center justify-space-between">
                            <span class="ps-summary-label">{{ $t("profit") }}</span>
                            <span
                              class="ps-margin-pill"
                              :class="profitPercent >= 0 ? 'ps-margin-pill--pos' : 'ps-margin-pill--neg'"
                              style="font-size: 9.5px; padding: 1px 5px;"
                            >
                              {{ profitPercent >= 0 ? '+' : '' }}{{ profitPercent }}%
                            </span>
                          </div>
                          <div class="ps-summary-value ps-summary-value--success text-truncate">
                            {{ total_summary.profit | NumberFormat }} ₭
                          </div>
                        </div>
                      </div>
                    </v-col>
                  </v-row>
                </v-col>

                <!-- Actions: Add Stock -->
                <v-col cols="12" lg="4" class="d-flex align-center justify-end" style="gap: 8px;">
                  <v-btn
                    color="primary"
                    large
                    depressed
                    @click="addStock()"
                    :disabled="!desserts.length"
                    :loading="overlay"
                    class="font-weight-bold px-5 rounded-lg flex-grow-1 flex-lg-grow-0"
                  >
                    <v-icon left small>fal fa-box-up</v-icon>
                    {{ $t("add_stock") }}
                  </v-btn>
                </v-col>

              </v-row>

              <!-- Toggle Currency Breakdown if multiple currencies exist -->
              <div v-if="Object.keys(summary).length > 1" class="mt-2 pt-2 border-top">
                <div class="d-flex align-center justify-space-between cursor-pointer" @click="toggleCurrencyBreakdown">
                  <span class="text-caption font-weight-bold primary--text">
                    <v-icon left x-small color="primary">fal fa-coins</v-icon>
                    {{ $t("currency_summary") }} ({{ Object.keys(summary).length }})
                  </span>
                  <v-icon small color="primary">{{ showCurrencyBreakdown ? 'fal fa-chevron-up' : 'fal fa-chevron-down' }}</v-icon>
                </div>
                <v-expand-transition>
                  <div v-show="showCurrencyBreakdown" class="d-flex flex-wrap mt-2" style="gap: 8px;">
                    <v-card v-for="(value, curr) in summary" :key="curr" outlined elevation="0" class="ap-metric-card pa-2 text-caption" style="min-width: 140px;">
                      <div class="font-weight-bold primary--text">{{ curr }}</div>
                      <div>{{ $t("price_buy") }}: <span class="error--text font-weight-medium">{{ value.price_buy | NumberFormat }}</span></div>
                      <div>{{ $t("price_sale") }}: <span class="info--text font-weight-medium">{{ value.price_sale | NumberFormat }}</span></div>
                    </v-card>
                  </div>
                </v-expand-transition>
              </div>

            </div>
          </v-card>
        </v-col>
      </v-row>
    </div>

    <!-- View Item Dialog -->
    <app-custom-dialog v-model="dialog" :footer="false" title="description">
      <div slot="activator">
        <AppDetail :item="form" />
      </div>
    </app-custom-dialog>

    <!-- Add/Edit Item Dialog -->
    <app-custom-dialog
      v-model="dialogAdd"
      title="add_stock"
      @submit="stockPrepare(form)"
    >
      <div slot="activator">
        <CurrencyInput label="quantity" v-model="form.qty" />
        <CurrencyInput
          label="price_buy"
          v-model="form.price_buy"
          :suffix="currency"
          class="mt-3"
        />

        <CurrencyInput
          label="price_sale"
          v-model="form.price_sale"
          :suffix="currency"
          class="mt-3"
        />
        <DatePicker
          label="expired_date"
          class="mt-3"
          v-model="form.expired_date"
        />
        <TextSelect
          v-if="suppliers.length"
          label="supplier"
          v-model="form.vendor_id"
          itemValue="id"
          text="name"
          :items="suppliers"
          class="mt-3"
        />
      </div>
    </app-custom-dialog>

    <!-- Purchase Variant Modal -->
    <PurchaseVariantModal
      v-if="selectedProduct"
      :show.sync="showVariantModal"
      :product="selectedProduct"
      :suppliers="suppliers"
      :purchase-items="desserts"
      @add-to-purchase="handleAddToPurchase"
      @update:show="handleCloseVariantModal"
    />

    <!-- Loading Overlay -->
    <app-loading :overlay="overlay" />
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import AppDetail from "@/components/products/details";
import { confirm, success, toast } from "@/controllers/alert";
import _ from "lodash";
import { myMixin } from "@/mixin/main";
import CurrencyInput from "@/components/CurrencyInput";
import TextSelect from "@/components/global/TextSelect";
import DatePicker from "@/components/global/DatePicker";
import PurchaseVariantModal from "@/components/products/PurchaseVariantModal";
const today = new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
  .toISOString()
  .substr(0, 10);

export default {
  mixins: [myMixin],
  components: {
    AppDetail,
    CurrencyInput,
    TextSelect,
    DatePicker,
    PurchaseVariantModal,
  },
  data() {
    return {
      dialogAdd: false,
      dialog: false,
      overlay: false,
      loadingProductId: null,
      search: "",
      category_id: {
        id: 0,
        la_name: "ທັງໝົດ",
        en_name: "All",
        category_display: "ທັງໝົດ",
      },
      selectedCategoryIndex: 0,
      selectedSupplier: null,
      stock_status_filter: "all",
      desserts: [],
      update: false,
      showVariantModal: false,
      selectedProduct: null,
      form: {
        expired_date: today,
      },
      headers: [
        { text: "#", value: "index", align: "center", sortable: false, width: "48px" },
        { text: this.$t("name"), value: "name", align: "left", sortable: false },
        { text: this.$t("price_buy") || "ຕົ້ນທຶນ", value: "price_buy", align: "right", width: "135px" },
        { text: this.$t("quantity"), value: "qty", align: "center", width: "140px" },
        { text: this.$t("price_sale"), value: "price_sale", align: "right", width: "120px" },
        { text: this.$t("profit") || "Margin", value: "margin", align: "center", width: "95px" },
        { text: this.$t("total_price"), value: "total", align: "right", width: "140px" },
        { text: this.$t("manage"), value: "actions", align: "center", sortable: false, width: "110px" },
      ],
      showCurrencyBreakdown: false,
    };
  },

  async created() {
    await this.fetchCategory();
    this.fetchItem();
    this.fetchVendor();
    this.fetchBranch();
    // Initialize search debouncer
    this.searchDebounce = _.debounce(this.fetchItem, 500);
    // Ensure all items have uniqueKey
    this.ensureUniqueKeys();
  },

  watch: {
    category_id: {
      handler(newVal, oldVal) {
        const newId = newVal?.id ?? newVal ?? 0;
        const oldId = oldVal?.id ?? oldVal ?? 0;
        if (newId !== oldId) {
          const idx = this.categories.findIndex((c) => c.id === newId);
          if (idx !== -1) {
            this.selectedCategoryIndex = idx;
          }
          this.fetchItem();
        }
      },
      deep: true,
    },
  },

  computed: {
    ...mapGetters({
      user: "auth/user",
      suppliers: "index/suppliers",
      branches: "index/branches",
      product_purchase: "index/product_purchase",
      category: "index/category",
    }),

    categories() {
      const all = {
        id: 0,
        la_name: "ທັງໝົດ",
        en_name: "All",
        th_name: "ทั้งหมด",
        cn_name: "全部",
      };
      const list = this.category?.length ? [all, ...this.category] : [all];
      return this.mapCategoriesForSelect(list);
    },

    poRefDate() {
      const d = new Date();
      const yr = d.getFullYear();
      const mo = String(d.getMonth() + 1).padStart(2, "0");
      return `${yr}${mo}`;
    },

    todayFormatted() {
      const d = new Date();
      return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
    },

    currentBranchName() {
      const b = this.branches?.find((x) => x.id === this.user?.branch_id);
      return b?.name || this.user?.branch?.name || "Main Branch";
    },

    currencySymbol() {
      return this.currency || "₭";
    },

    total_units() {
      return this.desserts.reduce((sum, item) => sum + (parseFloat(item.qty) || 0), 0);
    },

    filtered_products() {
      const data = this.product_purchase?.data || [];
      // Guarantee only track_stock products are shown (both backend query & frontend guard)
      const trackedData = data.filter(
        (p) => p.track_stock === 1 || p.track_stock === "1" || p.track_stock === true
      );

      if (this.stock_status_filter === "low") {
        return trackedData.filter((p) => p.stock > 0 && p.stock <= 10);
      }
      if (this.stock_status_filter === "out") {
        return trackedData.filter((p) => p.stock <= 0);
      }
      return trackedData;
    },

    stockStatusOptions() {
      return [
        {
          value: "all",
          text: this.$t("all") || "ທັງໝົດ",
          icon: "fal fa-boxes",
          color: "primary",
        },
        {
          value: "low",
          text: this.$t("low_stock") || "ໃກ້ໝົດ",
          icon: "fal fa-exclamation-circle",
          color: "warning",
        },
        {
          value: "out",
          text: this.$t("out_of_stock") || "ໝົດ",
          icon: "fal fa-times-circle",
          color: "error",
        },
      ];
    },

    total_buy() {
      let total = 0;
      this.desserts.forEach((item) => {
        // Since backend now stores original prices, no conversion needed
        total += item.price_buy * item.qty;
      });
      return total;
    },

    // sum price buy with qty for each item
    sum_price_buy() {
      let total = 0;
      this.desserts.forEach((item) => {
        // Since backend now stores original prices, no conversion needed
        total += item.price_buy * item.qty;
      });
      return total;
    },

    sum_price_sale() {
      let total = 0;
      this.desserts.forEach((item) => {
        // Since backend now stores original prices, no conversion needed
        total += item.price_sale * item.qty;
      });
      return total;
    },

    sum_profit() {
      return this.sum_price_sale - this.sum_price_buy;
    },

    profitPercent() {
      if (!this.total_summary || this.total_summary.price_sale <= 0) return 0;
      const percent =
        (this.total_summary.profit / this.total_summary.price_sale) * 100;
      return Math.max(-100, Math.min(100, Number(percent.toFixed(1))));
    },

    total_summary() {
      // loop through desserts and calculate total summary
      const summary = {
        price_buy: 0,
        price_sale: 0,
        profit: 0,
        qty: 0,
      };
      this.desserts.forEach((item) => {
        // Since backend now stores original prices, no conversion needed
        summary.price_buy += item.price_buy * item.qty;
        summary.price_sale += item.price_sale * item.qty;
        summary.qty += parseFloat(item.qty);
        summary.profit += (item.price_sale - item.price_buy) * item.qty;
      });

      return summary;
    },

    total_sale() {
      let total = 0;
      this.desserts.forEach((item) => {
        // Since backend now stores original prices, no conversion needed
        total += item.price_sale * item.qty;
      });
      return total;
    },

    total_profit() {
      return this.total_sale - this.total_buy;
    },

    currency() {
      if (this.form.currency) {
        return this.form.currency.code;
      }
      return "";
    },

    // Summary with improved structure
    summary() {
      const summary = {};
      this.desserts.forEach((item) => {
        const code = item.currency && item.currency.code ? item.currency.code : "";
        if (!code) return;
        if (!summary[code]) {
          summary[code] = {
            price_buy: 0,
            price_sale: 0,
            qty: 0,
          };
        }

        summary[code].price_buy += item.price_buy * item.qty;
        summary[code].price_sale += item.price_sale * item.qty;
        summary[code].qty += parseFloat(item.qty);
      });

      return summary;
    },
  },

  methods: {
    async clearSelected() {
      const { isConfirmed } = await confirm({
        props: this,
        text: "want_to_clear",
      });
      if (!isConfirmed) return;
      this.desserts = [];
      toast(this, "cleared_successfully");
    },
    copyBarcode(code) {
      if (!code) return;
      navigator.clipboard?.writeText(String(code));
      toast(this, "copy_success");
    },
    ...mapActions({
      fetch: "index/fetch",
    }),

    toggleCurrencyBreakdown() {
      this.showCurrencyBreakdown = !this.showCurrencyBreakdown;
    },

    async fetchCategory() {
      const body = {
        url: "categories",
        that: this,
        commit: "SET_CATEGORY",
      };
      await this.fetch(body);
    },

    async fetchItem() {
      this.overlay = true;
      const catId =
        this.category_id && this.category_id.id !== undefined
          ? this.category_id.id
          : this.category_id || 0;
      const catParam = catId && catId !== 0 ? `&cat_id=${catId}` : "";
      const body = {
        that: this,
        url: `products_all?q=${this.search}&per_page=50&track_stock=1&is_bundle=0${catParam}`,
        commit: "SET_PRODUCT_PURCHASE",
      };
      await this.fetch(body);
      this.overlay = false;
    },

    onSelectCategoryChip(cat) {
      this.category_id = cat;
      const idx = this.categories.findIndex((c) => c.id === cat.id);
      if (idx !== -1) {
        this.selectedCategoryIndex = idx;
      }
      this.fetchItem();
    },

    onCategoryDropdownChange(val) {
      if (val) {
        this.category_id = val;
        const idx = this.categories.findIndex((c) => c.id === (val.id ?? val));
        if (idx !== -1) {
          this.selectedCategoryIndex = idx;
        }
        this.fetchItem();
      }
    },

    getCartQty(product) {
      if (!product || !this.desserts) return 0;
      return this.desserts
        .filter((item) => item.id === product.id)
        .reduce((sum, item) => sum + (parseFloat(item.qty) || 0), 0);
    },

    getItemMargin(item) {
      if (!item || !item.price_sale || item.price_sale <= 0) return 0;
      const profit = item.price_sale - item.price_buy;
      return Math.round((profit / item.price_sale) * 100);
    },

    async fetchBranch() {
      const body = {
        that: this,
        url: "branches",
        commit: "SET_BRANCHES",
      };
      await this.fetch(body);
    },

    async fetchVendor() {
      const body = {
        that: this,
        url: "suppliers",
        commit: "SET_SUPPLIERS",
      };
      await this.fetch(body);
      if (this.suppliers?.length && !this.selectedSupplier) {
        this.selectedSupplier = this.suppliers[0].id;
      }
    },

    clearSearch() {
      this.search = "";
      this.fetchItem();
    },

    decrement(item) {
      if (item.qty <= 1) {
        // Ask for confirmation before removing
        this.confirmDeleteItem(item);
      } else {
        item.qty -= 1;
      }
    },

    increment(item) {
      item.qty += 1;
    },

    async confirmDeleteItem(item) {
      const { isConfirmed } = await confirm({
        props: this,
        text: "want_to_delete",
      });
      if (isConfirmed) {
        const index = this.desserts.findIndex((i) => i.id === item.id);
        if (index !== -1) {
          this.desserts.splice(index, 1);
          toast(this, "item_deleted_successfully");
        }
      }
    },

    async viewItem({ id }) {
      try {
        this.overlay = true;

        const { data } = await this.$http.get(`products/${id}`);
        if (data.status) {
          this.dialog = !this.dialog;
          this.form = data.data;
        }
        this.overlay = false;
      } catch (error) {
        this.overlay = false;
        console.error("Error fetching product details:", error);
        toast(this, "error_fetching_data", "error");
      }
    },

    editItem(item) {
      this.update = true;
      // Since backend now stores original prices, no conversion needed
      this.form = {
        ...item,
        price_buy: item.price_buy,
        price_sale: item.price_sale,
      };
      this.dialogAdd = true;
    },

    deleteItem(index) {
      this.desserts.splice(index, 1);
      toast(this, "item_deleted_successfully");
    },

    getVariantDisplayName(variant) {
      if (!variant || !variant.options) return "";

      const options = Object.entries(variant.options)
        .map(([key, value]) => `${key}: ${value}`)
        .join(", ");

      return options;
    },

    isProductInCart(product) {
      // Check if this product is already in the purchase cart
      return this.desserts.some((item) => item.id === product.id);
    },

    async setToPurchase(search, targetProduct = null) {
      try {
        if (!search) return;
        if (targetProduct) {
          this.loadingProductId = targetProduct.id;
        } else {
          this.overlay = true;
        }

        const { data } = await this.$http.get(`products_barcode/${search}`);
        if (data.status) {
          const _data = data.data;

          // Check if product has track_stock disabled and show warning
          if (_data.track_stock === 0) {
            toast(this, "product_track_stock_disabled", "warning");
          }

          // Check if product has variations
          if (
            _data.has_variations &&
            _data.options &&
            _data.options.length > 0
          ) {
            // Fetch complete product details from API before showing modal
            try {
              const { data: productData } = await this.$http.get(
                `/products/${_data.id}`
              );
              if (productData.status) {
                this.selectedProduct = productData.data;
                this.showVariantModal = true;
              } else {
                toast(this, "error_fetching_product_details", "error");
              }
            } catch (error) {
              console.error("Error fetching product details:", error);
              toast(this, "error_fetching_product_details", "error");
            }
            this.overlay = false;
            this.loadingProductId = null;
            return;
          }

          // Check for duplicate item
          const duplicate = this.desserts.find((item) => item.id === _data.id);

          if (duplicate) {
            duplicate.qty += 1;
            toast(this, "quantity_increased");
          } else {
            // Add new item to cart
            const uniqueKey = this.generateUniqueKey(_data);
            this.desserts.push({ ..._data, qty: 1, uniqueKey });
            toast(this, "item_added_to_cart");
          }
        }
        this.overlay = false;
        this.loadingProductId = null;
      } catch (error) {
        this.overlay = false;
        this.loadingProductId = null;
        console.error("Error adding product:", error);
        toast(this, "error_adding_product", "error");
      }
    },

    stockPrepare(form) {
      // Find the product in desserts array with matching id
      const index = this.desserts.findIndex((item) => item.id === form.id);

      if (index !== -1) {
        const value = this.desserts[index];

        // Update properties
        value.qty = parseFloat(form.qty);

        // Since backend now stores original prices, no conversion needed
        value.price_buy = form.price_buy;
        value.price_sale = form.price_sale;
        // Only update expired_date if the product doesn't have one, otherwise preserve the original
        value.expired_date = value.expired_date || form.expired_date;
        value.vendor_id = this.getVendorId(form.vendor_id);

        // Close dialog and show success message
        this.dialogAdd = false;
        this.update = false;
        toast(this, "item_updated_successfully");
      } else {
        console.error("Product not found in purchase list");
        toast(this, "item_not_found", "error");
      }
    },

    async addStock() {
      try {
        if (!this.desserts.length) {
          toast(this, "no_items_to_purchase", "warning");
          return;
        }

        const { isConfirmed } = await confirm({
          props: this,
          text: "want_to_purchase",
        });

        if (isConfirmed) {
          this.overlay = true;

          // Prepare data for API
          let arr = this.desserts.map((item) => {
            const baseData = {
              product_id: item.id,
              qty: item.qty,
              price_buy: item.price_buy,
              price_sale: item.price_sale,
              expired_date: item.expired_date || null,
              vendor_id: this.getVendorId(item.vendor_id || this.selectedSupplier),
              currency_id: item.currency_id,
              exchange_rate: item.exchange_rate || 1,
            };

            // Add variant information if it's a variant product
            if (
              item.has_variations &&
              item.variant &&
              item.selectedVariationOptions
            ) {
              baseData.variant_id = item.variant.id;
            }

            return baseData;
          });

          const form = { data: arr };

          // Submit to API
          const { data } = await this.$http.post("stock", form);

          if (data.status) {
            this.fetchItem();
            this.desserts = [];
            toast(this, data.message, "success");
          } else {
            toast(this, data.message || "error_adding_stock", "error");
          }
          this.overlay = false;
        }
      } catch (error) {
        this.overlay = false;
        console.error("Error adding stock:", error);
        toast(this, "error_adding_stock", "error");
      }
    },

    // Helper method to extract vendor ID
    getVendorId(vendor) {
      if (!vendor) return null;
      return typeof vendor === "object" ? vendor.id : vendor;
    },

    // Helper method to get exchange rate for a currency
    getExchangeRate(currencyCode) {
      // Find the first item with this currency to get its exchange rate
      const item = this.desserts.find(
        (item) => item.currency && item.currency.code === currencyCode
      );
      return item ? item.exchange_rate : 1;
    },

    // Generate unique key for VDataTable items
    generateUniqueKey(item) {
      // For variant products, include variant information in the key
      if (
        item.has_variations &&
        item.variant &&
        item.selectedVariationOptions
      ) {
        const variantKey = JSON.stringify(item.selectedVariationOptions);
        return `${item.id}-${item.variant.id}-${variantKey}`;
      }
      // For non-variant products, use product ID
      return item.id.toString();
    },

    // Ensure all items in desserts array have uniqueKey
    ensureUniqueKeys() {
      this.desserts.forEach((item) => {
        if (!item.uniqueKey) {
          item.uniqueKey = this.generateUniqueKey(item);
        }
      });
    },

    // Handle clicking on a product in the list
    async handleProductClick(item) {
      if (this.loadingProductId) return;

      // Check if product has variations
      if (item.has_variations) {
        // Fetch complete product details from API before showing modal
        try {
          this.loadingProductId = item.id;
          const { data } = await this.$http.get(`/products/${item.id}`);
          if (data.status) {
            this.selectedProduct = data.data;
            this.showVariantModal = true;
          } else {
            toast(this, "error_fetching_product_details", "error");
          }
        } catch (error) {
          console.error("Error fetching product details:", error);
          toast(this, "error_fetching_product_details", "error");
        } finally {
          this.loadingProductId = null;
        }
      } else {
        // Direct add to purchase for products without variations
        this.setToPurchase(item.code, item);
      }
    },

    // Handle closing the variant modal
    handleCloseVariantModal() {
      this.showVariantModal = false;
      this.selectedProduct = null;
    },

    // Handle adding variant product to purchase
    handleAddToPurchase(purchaseData) {
      try {
        const {
          product,
          variant,
          quantity,
          price_buy,
          price_sale,

          expired_date,
          vendor_id,
          selectedVariationOptions,
        } = purchaseData;

        // Create purchase item with variant information
        const purchaseItem = {
          ...product,
          qty: quantity,
          price_buy: price_buy, // Since backend now stores original prices, no conversion needed
          price_sale: price_sale, // Since backend now stores original prices, no conversion needed
          expired_date: expired_date,
          vendor_id: vendor_id,
          variant: variant, // Store variant information
          selectedVariationOptions: selectedVariationOptions, // Store selected options
        };

        // Generate unique key for the item
        purchaseItem.uniqueKey = this.generateUniqueKey(purchaseItem);

        // Check for duplicate item (same product and variant)
        const duplicate = this.desserts.find((item) => {
          if (item.id !== product.id) return false;

          // For variant products, check if it's the same variant
          if (product.has_variations && variant) {
            return (
              JSON.stringify(item.selectedVariationOptions) ===
              JSON.stringify(selectedVariationOptions)
            );
          }

          return true; // For non-variant products
        });

        if (duplicate) {
          duplicate.qty += quantity;
          toast(this, "quantity_increased");
        } else {
          // Add new item to purchase list
          this.desserts.push(purchaseItem);
          toast(this, "item_added_to_purchase");
        }

        // Reset modal state
        this.selectedProduct = null;
        this.showVariantModal = false;
      } catch (error) {
        console.error("Error adding variant to purchase:", error);
        toast(this, "error_adding_variant", "error");
      }
    },
  },
};
</script>

<style scoped>
.purchase-page-wrapper {
  height: calc(100vh - 65px); /* Subtract app bar height */
  display: flex;
  flex-direction: column;
}

.purchase-page-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 16px;
  gap: 16px;
  overflow: hidden;
}

.cart-content-area {
  flex: 1;
  min-height: 0; /* Important for flex child scrolling */
  overflow-y: auto;
  border-radius: 12px;
}

/* PosSmart v2 Section Cards (matches manage/add-product.vue & products/card.vue) */
.ap-card {
  border-radius: 16px !important;
  border: 1px solid #e2e8f0 !important;
  box-shadow: none !important;
  overflow: hidden;
  background-color: #ffffff !important;
}

.ap-card-title {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  font-size: 14px;
  font-weight: 600;
}

.ap-card-accent {
  width: 3px;
  height: 15px;
  border-radius: 2px;
  background: var(--v-primary-base, #5b39c9);
  display: inline-block;
  flex-shrink: 0;
}

.ap-card-subtitle {
  font-size: 11.5px;
  font-weight: 400;
  color: #94a3b8;
}

.ap-metric-card {
  border-radius: 12px !important;
  border: 1px solid #e2e8f0 !important;
  box-shadow: none !important;
}

.cart-summary-area {
  flex-shrink: 0;
  background-color: #ffffff;
  color: inherit;
  display: flex;
  flex-direction: column;
}

.product-card {
  height: 100%;
  min-height: 0;
}

.product-card >>> .v-card__text {
  flex: 1 1 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.purchase-main-card {
  height: 100%;
  min-height: 0;
}

.filter-controls {
  flex-shrink: 0;
}

.product-list-container {
  flex: 1 1 0;
  min-height: 0;
  height: 100%;
  overflow-y: auto;
  border-radius: 12px;
}

.border-bottom {
  border-bottom: 1px solid #e2e8f0 !important;
}

.category-chips-wrapper {
  overflow-x: auto;
}

.category-chips-wrapper >>> .v-slide-group__content {
  padding: 2px 0;
}

.product-list-item {
  transition: all 0.2s ease;
  border: 1px solid rgba(0, 0, 0, 0.06);
  cursor: pointer;
}

.product-list-item:hover {
  border-color: #5B39C9;
  background-color: rgba(91, 57, 201, 0.03);
}

.product-list-item.product-in-cart {
  background-color: rgba(91, 57, 201, 0.08);
  border-color: #5B39C9;
}

.border-top {
  border-top: 1px solid #e2e8f0 !important;
}

.bg-slate-50 {
  background-color: #f8fafc !important;
}

/* ===================================================
   Purchased Items Data List (matches products.vue design base)
   =================================================== */

.purchase-table >>> .v-data-table__wrapper table {
  border-collapse: collapse;
}

.purchase-table >>> .v-data-table__wrapper table thead th {
  color: #64748b;
  font-weight: 700;
  font-size: 10.5px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  background-color: transparent !important;
  background: transparent !important;
  box-shadow: none !important;
  border-bottom: 1px solid #e2e8f0;
  height: 38px;
  white-space: nowrap;
}

.purchase-table >>> .v-data-table__wrapper table tbody tr {
  transition: background-color 0.15s ease;
}

.purchase-table >>> .v-data-table__wrapper table tbody tr:hover {
  background-color: #f8fafc !important;
}

.purchase-table >>> .v-data-table__wrapper table tbody td {
  border-bottom: 1px solid #f1f5f9;
  padding: 8px 12px;
}

.ps-row-index {
  font-size: 12px;
  color: #94a3b8;
  font-variant-numeric: tabular-nums;
  font-weight: 500;
}

.ps-row-thumb {
  position: relative;
  width: 34px;
  height: 34px;
  display: inline-flex;
}

.ps-thumb-avatar {
  background: #f1f5f9 !important;
  border: 1px solid #e2e8f0;
  overflow: hidden;
}

.ps-row-name {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.ps-product-name {
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ps-product-code {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-family: monospace;
  color: #94a3b8;
  font-variant-numeric: tabular-nums;
}

.ps-copy-code {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px;
  border: 0;
  background: transparent;
  color: #94a3b8;
  cursor: pointer;
  border-radius: 4px;
  transition: color 0.15s ease;
}

.ps-copy-code:hover {
  color: #5b39c9;
}

.ps-category-pill {
  font-size: 11px;
  padding: 2px 7px;
  border-radius: 999px;
  width: fit-content;
  color: #4338ca;
  background: #eef2ff;
  border: 1px solid #c7d2fe;
  white-space: nowrap;
  font-weight: 600;
}

/* Cost Input Box */
.ps-cost-input-box {
  display: inline-flex;
  align-items: center;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 0 6px;
  transition: all 0.15s ease;
  width: 100px;
}

.ps-cost-input-box:focus-within {
  border-color: #5b39c9;
  box-shadow: 0 0 0 1px #5b39c9;
  background: #ffffff;
}

.ps-cost-input {
  width: 100%;
  height: 28px;
  border: none;
  background: transparent;
  text-align: right;
  font-size: 13px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: #1e293b;
  outline: none;
}

/* Quantity Stepper */
.ps-qty-stepper {
  display: inline-flex;
  align-items: center;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 2px;
  gap: 2px;
}

.ps-qty-btn {
  width: 24px;
  height: 24px;
  border-radius: 6px;
  border: none;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  cursor: pointer;
  transition: all 0.15s ease;
  outline: none;
}

.ps-qty-btn:hover {
  color: #5b39c9;
  background: rgba(91, 57, 201, 0.08);
}

.ps-qty-btn--plus {
  color: #5b39c9;
}

.ps-qty-input {
  width: 38px;
  height: 24px;
  border: none;
  background: transparent;
  text-align: center;
  font-size: 13px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: #1e293b;
  outline: none;
}

/* Sale Price & Total Values */
.ps-price-value {
  font-size: 13px;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  color: #64748b;
}

.ps-total-value {
  font-size: 13.5px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: #5b39c9;
}

/* Margin Pill */
.ps-margin-pill {
  font-size: 10.5px;
  font-weight: 700;
  padding: 2px 7px;
  border-radius: 999px;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.ps-margin-pill--pos {
  background-color: #d1fae5;
  color: #047857;
  border: 1px solid #a7f3d0;
}

.ps-margin-pill--neg {
  background-color: #fee2e2;
  color: #dc2626;
  border: 1px solid #fecdd3;
}

/* Row Action Buttons (matches products.vue .ps-act) */
.ps-row-actions {
  display: flex;
  align-items: center;
  gap: 5px;
}

.ps-act {
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  color: #64748b;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  cursor: pointer;
  transition: all 0.15s ease;
  outline: none;
}

.ps-act:hover {
  color: #5b39c9;
  border-color: #5b39c9;
  background: rgba(91, 57, 201, 0.06);
}

.ps-act--danger:hover {
  color: #ef4444;
  border-color: #ef4444;
  background: #fef2f2;
}

/* Empty State */
.ps-empty {
  color: #94a3b8;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 56px 16px;
}

.ps-empty-icon {
  color: #cbd5e1 !important;
}

.ps-empty-text {
  font-size: 14px;
  color: #475569;
}

.ps-empty-subtext {
  font-size: 12px;
  color: #94a3b8;
}

/* ===================================================
   Bottom HUD: PosSmart Branding Summary Cards
   =================================================== */
.ps-summary-card {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  transition: all 0.15s ease;
  min-width: 0;
}

.ps-summary-card:hover {
  border-color: #cbd5e1;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.ps-summary-icon {
  width: 36px;
  height: 36px;
  flex: 0 0 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid transparent;
}

.ps-summary-icon--primary {
  background: rgba(91, 57, 201, 0.1);
  color: #5b39c9;
  border-color: rgba(91, 57, 201, 0.2);
}

.ps-summary-icon--danger {
  background: #fef2f2;
  color: #dc2626;
  border-color: #fecdd3;
}

.ps-summary-icon--info {
  background: #f0fdf4;
  color: #0284c7;
  border-color: #bae6fd;
}

.ps-summary-icon--success {
  background: #ecfdf5;
  color: #059669;
  border-color: #a7f3d0;
}

.ps-summary-meta {
  min-width: 0;
  flex: 1 1 auto;
}

.ps-summary-label {
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #64748b;
  white-space: nowrap;
  line-height: 1.2;
  margin-bottom: 2px;
}

.ps-summary-value {
  font-size: 14px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  color: #1e293b;
  line-height: 1.2;
}

.ps-summary-subval {
  font-size: 12px;
  font-weight: 600;
  color: #94a3b8;
}

.ps-summary-value--danger {
  color: #dc2626;
}

.ps-summary-value--info {
  color: #0284c7;
}

.ps-summary-value--success {
  color: #059669;
}

/* Enhanced Summary Section Styling */
.summary-section {
  background: transparent;
  border-radius: 12px;
  padding: 16px;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.currency-summary-cards {
  margin-bottom: 20px;
}

.currency-card {
  border: 1px solid #555;
  transition: all 0.3s ease;
}

.currency-header {
  color: white;
  font-weight: 700;
  font-size: 16px;
  padding: 16px 20px;
  border-radius: 8px 8px 0 0;
}

.currency-content {
  padding: 20px;
  background: #3a3a3a;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding: 8px 0;
  border-bottom: 1px solid #555;
}

.summary-row:last-child {
  border-bottom: none;
  margin-bottom: 0;
}

.summary-label {
  color: #ccc;
  font-weight: 500;
  font-size: 14px;
}

.summary-value {
  font-weight: 700;
  font-size: 16px;
}

.summary-value.cost {
  color: #ff6b6b;
}

.summary-value.sale {
  color: #ffffff;
}

.summary-value.profit.positive {
  color: #51cf66;
}

.summary-value.profit.negative {
  color: #ff6b6b;
}

/* Compact Currency Summary */
.currency-summary-compact {
  margin-bottom: 12px;
}

.currency-summary-title {
  color: white;
  font-weight: 700;
  font-size: 16px;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  border: 1px solid #555;
  transition: all 0.3s ease;
}

.currency-summary-title:hover {
  background: rgba(255, 255, 255, 0.08);
  transform: translateY(-1px);
}

.title-left {
  display: flex;
  align-items: center;
}

.toggle-icon {
  transition: transform 0.3s ease;
  font-size: 16px;
}

.rotate-180 {
  transform: rotate(180deg);
}

.currency-cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}

.currency-card-compact {
  background: linear-gradient(135deg, #3a3a3a 0%, #4a4a4a 100%);
  border: 1px solid #555;
  border-radius: 8px;
  padding: 12px;
  transition: all 0.3s ease;
}

.currency-card-compact:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.currency-name {
  color: #667eea;
  font-weight: 700;
  font-size: 14px;
  margin-bottom: 8px;
  text-align: center;
  padding: 4px 8px;
  background: rgba(102, 126, 234, 0.1);
  border-radius: 4px;
}

.currency-values {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.currency-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
}

.currency-label {
  color: #ccc;
  font-weight: 500;
  font-size: 12px;
}

.currency-value {
  font-weight: 700;
  font-size: 13px;
}

.currency-value.cost {
  color: #ff6b6b;
}

.currency-value.sale {
  color: #ffffff;
}

.currency-value.profit.positive {
  color: #51cf66;
}

.currency-value.profit.negative {
  color: #ff6b6b;
}

/* Total Summary Card */
.total-summary-card {
  border: 1px solid #555;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
}

.total-header {
  color: white;
  font-weight: 700;
  font-size: 16px;
  padding: 16px;
  border-radius: 8px 8px 0 0;
}

.total-content {
  padding: 16px;
  background: #3a3a3a;
}

.total-summary-grid {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.total-summary-compact {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.total-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  border: 1px solid #555;
}

.total-row-compact {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 6px;
  border: 1px solid #555;
}

.total-label {
  color: #ccc;
  font-weight: 600;
  font-size: 16px;
}

.total-value {
  font-weight: 700;
  font-size: 18px;
}

.total-value.cost {
  color: #ff6b6b;
}

.total-value.sale {
  color: #ffffff;
}

.total-value.profit.positive {
  color: #51cf66;
}

.total-value.profit.negative {
  color: #ff6b6b;
}

.add-stock-btn {
  box-shadow: 0 4px 20px rgba(102, 126, 234, 0.4);
  transition: all 0.3s ease;
  height: 60px;
  font-size: 16px;
}

.add-stock-btn:disabled {
  box-shadow: none;
  transform: none;
}

/* Enhanced Total Summary Card */
.total-summary-card-enhanced {
  border: 1px solid #555;
  border-radius: 12px;
}

.total-header-enhanced {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 12px 12px 0 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-icon {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  padding: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-text {
  display: flex;
  flex-direction: column;
}

.header-title {
  font-weight: 700;
  font-size: 16px;
  line-height: 1.2;
}

.header-subtitle {
  font-size: 12px;
  opacity: 0.9;
  font-weight: 500;
}

.header-badge {
  display: flex;
  align-items: center;
}

.total-content-enhanced {
  padding: 14px;
  background: transparent;
  padding-bottom: 0px;
}

/* Light/Dark theme adaptations - Vuetify theme root is on body with theme--light or theme--dark */
::v-deep(.theme--light) .cart-summary-area {
  background: #ffffff;
  color: #2b2b2b;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.08);
}

::v-deep(.theme--dark) .cart-summary-area {
  background: #2d2d2d;
  color: #ffffff;
}

::v-deep(.theme--light) .summary-section {
  background: #ffffff;
}

::v-deep(.theme--dark) .summary-section {
  background: #2d2d2d;
}

/* Light theme cards */
::v-deep(.theme--light) .currency-summary-title {
  color: #2b2b2b;
  background: #f8fafc;
  border-color: #e5e7eb;
}

::v-deep(.theme--light) .currency-card-compact {
  background: linear-gradient(135deg, #ffffff 0%, #fafafa 100%);
  border-color: #e5e7eb;
}

::v-deep(.theme--light) .currency-name {
  color: #438EF2;
  background: rgba(79, 70, 229, 0.06);
}

::v-deep(.theme--light) .currency-label,
::v-deep(.theme--light) .summary-label {
  color: #6b7280;
}

::v-deep(.theme--light) .currency-value.sale,
::v-deep(.theme--light) .summary-value.sale {
  color: #111827;
}

::v-deep(.theme--light) .summary-item {
  background: #f8fafc;
  border-color: #e5e7eb;
}

::v-deep(.theme--light) .total-summary-card-enhanced {
  border-color: #e5e7eb;
}

::v-deep(.theme--light) .total-content-enhanced {
  background: #ffffff;
}

/* Dark theme keep rich contrast */
::v-deep(.theme--dark) .currency-summary-title {
  color: #ffffff;
  background: rgba(255, 255, 255, 0.05);
  border-color: #555;
}

::v-deep(.theme--dark) .currency-card-compact {
  background: linear-gradient(135deg, #3a3a3a 0%, #4a4a4a 100%);
  border-color: #555;
}

::v-deep(.theme--dark) .total-content-enhanced {
  background: #3a3a3a;
}

.total-layout {
  display: grid;
  grid-template-columns: 3fr 2fr; /* 60% / 40% */
  gap: 16px;
  align-items: center;
}

.total-left {
  min-width: 0;
}

.total-right {
  min-width: 0;
}

.summary-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  margin-bottom: 16px;
}

.summary-progress {
  margin: 4px 0 20px 0;
}

.progress-labels {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  color: #ccc;
  margin-bottom: 6px;
}

.summary-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 10px;
  border: 1px solid #555;
  transition: all 0.3s ease;
}

.summary-item:hover {
  background: rgba(255, 255, 255, 0.08);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.summary-icon {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  padding: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 32px;
}

.summary-details {
  flex: 1;
  display: flex;
  flex-direction: row;
  gap: 4px;
  justify-content: space-between;
  align-items: center;
}

.summary-label {
  color: #ccc;
  font-weight: 500;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.summary-value {
  font-weight: 700;
  font-size: 16px;
  line-height: 1.2;
}

.summary-value.cost {
  color: #ff6b6b;
}

.summary-value.sale {
  color: #ffffff;
}

.summary-value.profit.positive {
  color: #51cf66;
}

.summary-value.profit.negative {
  color: #ff6b6b;
}

/* Action Section */
.action-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.add-stock-btn-enhanced {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  box-shadow: 0 4px 20px rgba(102, 126, 234, 0.4);
  transition: all 0.3s ease;
  height: 48px;
  font-size: 14px;
  border-radius: 10px;
  text-transform: none;
  letter-spacing: 0.5px;
}

.add-stock-btn-enhanced:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 32px rgba(102, 126, 234, 0.6);
}

.add-stock-btn-enhanced:disabled {
  background: #666;
  box-shadow: none;
  transform: none;
}

.action-hint {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #81c784;
  font-size: 12px;
  font-weight: 500;
  padding: 8px;
  background: rgba(129, 199, 132, 0.1);
  border-radius: 8px;
  border: 1px solid rgba(129, 199, 132, 0.2);
}

/* Responsive adjustments */
@media (max-width: 960px) {
  .total-layout {
    grid-template-columns: 1fr; /* stack on small screens */
  }

  .summary-grid {
    gap: 10px;
  }

  .summary-item {
    padding: 10px;
    gap: 10px;
  }

  .summary-icon {
    min-width: 28px;
    height: 28px;
    padding: 5px;
  }

  .summary-value {
    font-size: 14px;
  }

  .add-stock-btn-enhanced {
    height: 44px;
    font-size: 13px;
  }
}

/* Data table header styling */
.purchase-table >>> .v-data-table__wrapper table thead th {
  color: #64748b;
  font-weight: 600;
  font-size: 12px;
  background-color: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}

.theme--dark .purchase-table >>> .v-data-table__wrapper table thead th {
  color: #94a3b8;
  background-color: #252538;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

/* ===================================================
   Active Category Chip Styling
   =================================================== */
.category-chips-wrapper >>> .v-chip.v-chip--active,
.category-chips-wrapper >>> .v-chip.v-chip--active .v-chip__content,
.category-chips-wrapper >>> .v-chip.primary,
.category-chips-wrapper >>> .v-chip.primary .v-chip__content,
.category-chips-wrapper >>> .active-category-chip,
.category-chips-wrapper >>> .active-category-chip .v-chip__content {
  color: #ffffff !important;
}

/* ===================================================
   Product Item Card (Stock Purchase Catalog)
   Matches interactive PosSmart v2 product card style
   =================================================== */
.product-item-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 12px;
  background-color: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.12s ease;
  user-select: none;
}

.product-item-card:hover {
  border-color: #5b39c9;
  box-shadow: 0 4px 12px rgba(91, 57, 201, 0.08);
}

.product-item-card:active {
  transform: scale(0.99);
}

.product-item-card:hover .product-name-text {
  color: #5b39c9;
}

.product-item-card:hover .product-add-btn {
  background-color: #5b39c9;
  color: #ffffff;
}

.product-item-card:hover .product-add-btn .add-btn-icon {
  color: #ffffff !important;
}

/* Active In-Cart State (Uniform small primary border) */
.product-item-card.is-in-cart {
  background-color: rgba(91, 57, 201, 0.04);
  border: 1.5px solid #5b39c9 !important;
  box-shadow: 0 2px 8px rgba(91, 57, 201, 0.12);
}

.product-item-card.is-in-cart .product-name-text {
  color: #5b39c9;
}

.product-item-card.is-disabled {
  opacity: 0.6;
  pointer-events: none;
}

/* Loading Fetch State (Circle loading in button plus icon only) */
.product-item-card.is-loading {
  cursor: wait;
}

.product-add-btn.btn-is-loading {
  background-color: rgba(91, 57, 201, 0.08);
  pointer-events: none;
}

/* Left Section: Thumbnail + Info */
.product-card-left {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  flex: 1 1 auto;
}

/* Thumbnail & In-Cart Badge */
.product-thumb-box {
  position: relative;
  flex-shrink: 0;
  width: 48px;
  height: 48px;
}

.product-thumb-box >>> .v-image,
.product-thumb-img {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  object-fit: cover;
  border: 1px solid #e2e8f0;
  overflow: hidden;
}

.cart-count-badge {
  position: absolute;
  top: -6px;
  right: -6px;
  min-width: 20px;
  height: 20px;
  padding: 0 5px;
  border-radius: 999px;
  background-color: #5b39c9;
  color: #ffffff;
  font-size: 10px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #ffffff;
  box-shadow: 0 2px 4px rgba(91, 57, 201, 0.3);
  z-index: 2;
}

/* Info Box */
.product-info-box {
  min-width: 0;
  flex: 1 1 auto;
}

.product-name-text {
  font-size: 13px;
  font-weight: 700;
  color: #1e293b;
  line-height: 1.3;
  transition: color 0.15s ease;
}

.product-sub-row {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 2px;
  font-size: 11px;
}

.product-code-mono {
  font-family: monospace;
  color: #94a3b8;
  font-weight: 500;
}

.product-dot-sep {
  color: #cbd5e1;
}

/* Stock Badges (Matches ps-prod-card__stock on sale page) */
.stock-pill {
  padding: 2px 7px;
  border-radius: 999px;
  font-size: 9.5px;
  font-weight: 800;
  line-height: 1.3;
  white-space: nowrap;
}

.stock-pill-out {
  background-color: #fee2e2;
  color: #dc2626;
}

.stock-pill-low {
  background-color: #fef3c7;
  color: #d97706;
}

.stock-pill-normal {
  background-color: #d1fae5;
  color: #047857;
}

/* Price Row */
.product-price-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
}

.buy-price-val {
  font-size: 12px;
  font-weight: 800;
  color: #5b39c9;
}

.sale-price-val {
  font-size: 10px;
  color: #94a3b8;
}

.variation-tag {
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 9px;
  font-weight: 600;
  background-color: #eef2ff;
  color: #4338ca;
  border: 1px solid #c7d2fe;
}

/* Right Section: Add Button */
.product-add-btn {
  width: 32px;
  height: 32px;
  min-width: 32px;
  border-radius: 10px;
  background-color: #f1f5f9;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
  outline: none;
}

.product-add-btn .add-btn-icon {
  color: #64748b !important;
  transition: color 0.15s ease;
}

.product-add-btn.btn-in-cart {
  background-color: #5b39c9;
  color: #ffffff;
}

.product-add-btn.btn-in-cart .add-btn-icon {
  color: #ffffff !important;
}

/* Dark theme overrides for product item card */
.theme--dark .product-item-card {
  background-color: #1e1e2d;
  border-color: #2d2d3f;
}

.theme--dark .product-item-card:hover {
  border-color: #6366f1;
  background-color: #252538;
}

.theme--dark .product-item-card.is-in-cart {
  background-color: rgba(91, 57, 201, 0.2);
  border: 1.5px solid #818cf8 !important;
  box-shadow: 0 2px 8px rgba(91, 57, 201, 0.25);
}

.theme--dark .product-name-text {
  color: #f1f5f9;
}

.theme--dark .product-add-btn {
  background-color: #2d2d3f;
  color: #94a3b8;
}

.theme--dark .product-add-btn .add-btn-icon {
  color: #94a3b8 !important;
}

.theme--dark .product-thumb-box >>> .v-image,
.theme--dark .product-thumb-img {
  border-color: #2d2d3f;
}

/* Dark theme overrides for PosSmart v2 section cards */
.theme--dark .ap-card {
  border-color: rgba(255, 255, 255, 0.12) !important;
  background-color: #1e1e2d !important;
}

.theme--dark .ap-card-subtitle {
  color: #64748b;
}

.theme--dark .ap-card-accent {
  background: var(--v-primary-base, #5b39c9);
}

.theme--dark .ap-metric-card,
.theme--dark .ps-summary-card {
  border-color: rgba(255, 255, 255, 0.12) !important;
  background-color: #252538 !important;
}

.theme--dark .ps-summary-value {
  color: #f1f5f9;
}

.theme--dark .ps-summary-subval {
  color: #94a3b8;
}

.theme--dark .ps-summary-icon--primary {
  background: rgba(129, 140, 248, 0.15);
  color: #818cf8;
  border-color: rgba(129, 140, 248, 0.3);
}

.theme--dark .ps-summary-icon--danger {
  background: rgba(239, 68, 68, 0.15);
  color: #f87171;
  border-color: rgba(239, 68, 68, 0.3);
}

.theme--dark .ps-summary-icon--info {
  background: rgba(56, 189, 248, 0.15);
  color: #38bdf8;
  border-color: rgba(56, 189, 248, 0.3);
}

.theme--dark .ps-summary-icon--success {
  background: rgba(16, 185, 129, 0.15);
  color: #34d399;
  border-color: rgba(16, 185, 129, 0.3);
}

.theme--dark .cart-summary-area {
  background-color: #1e1e2d !important;
  border-top-color: rgba(255, 255, 255, 0.12) !important;
}

.theme--dark .border-bottom {
  border-bottom-color: rgba(255, 255, 255, 0.12) !important;
}

.theme--dark .border-top {
  border-top-color: rgba(255, 255, 255, 0.12) !important;
}

.theme--dark .ps-product-name {
  color: #f1f5f9;
}

.theme--dark .ps-cost-input-box,
.theme--dark .ps-qty-stepper {
  background-color: #252538;
  border-color: rgba(255, 255, 255, 0.12);
}

.theme--dark .ps-cost-input,
.theme--dark .ps-qty-input {
  color: #f1f5f9;
}

.theme--dark .ps-qty-btn {
  background-color: #1e1e2d;
  color: #94a3b8;
}

.theme--dark .ps-price-value {
  color: #94a3b8;
}

.theme--dark .ps-act {
  background-color: #252538;
  border-color: rgba(255, 255, 255, 0.12);
  color: #94a3b8;
}

.theme--dark .ps-act:hover {
  color: #818cf8;
  border-color: #818cf8;
  background-color: rgba(91, 57, 201, 0.2);
}

.theme--dark .ps-act--danger:hover {
  color: #f87171;
  border-color: #f87171;
  background-color: rgba(239, 68, 68, 0.15);
}

.theme--dark .ps-empty-text {
  color: #cbd5e1;
}

/* Enhanced toolbar styling for dark theme */
.product-card >>> .v-toolbar {
  color: white;
  border-radius: 12px 12px 0 0;
}

.product-card >>> .v-toolbar .v-toolbar__title {
  color: white;
  font-weight: 700;
  font-size: 16px;
}

.product-card >>> .v-btn {
  color: white;
}

.v-card >>> .v-toolbar {
  background: linear-gradient(135deg, #3a3a3a 0%, #4a4a4a 100%);
  border-bottom: 2px solid #555;
}

.v-card >>> .v-toolbar .v-toolbar__title {
  color: white;
  font-weight: 700;
  font-size: 16px;
}

/* Action buttons styling */
.action-buttons {
  display: flex;
  gap: 8px;
  justify-content: center;
}

.action-buttons .v-btn {
  transition: all 0.2s ease;
  box-shadow: none !important;
}

.action-buttons .v-btn:hover {
  box-shadow: none !important;
}

/* ===================================================
   Custom Clean Scrollbar (Small width & subtle grey)
   =================================================== */
.cart-content-area,
.product-list-container,
.category-chips-wrapper,
.cart-content-area >>> .v-data-table__wrapper {
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 transparent;
}

.cart-content-area::-webkit-scrollbar,
.product-list-container::-webkit-scrollbar,
.category-chips-wrapper::-webkit-scrollbar,
.cart-content-area >>> .v-data-table__wrapper::-webkit-scrollbar {
  width: 5px;
  height: 5px;
}

.cart-content-area::-webkit-scrollbar-track,
.product-list-container::-webkit-scrollbar-track,
.category-chips-wrapper::-webkit-scrollbar-track,
.cart-content-area >>> .v-data-table__wrapper::-webkit-scrollbar-track {
  background: transparent;
}

.cart-content-area::-webkit-scrollbar-thumb,
.product-list-container::-webkit-scrollbar-thumb,
.category-chips-wrapper::-webkit-scrollbar-thumb,
.cart-content-area >>> .v-data-table__wrapper::-webkit-scrollbar-thumb {
  background-color: #cbd5e1;
  border-radius: 999px;
  transition: background-color 0.15s ease;
}

.cart-content-area::-webkit-scrollbar-thumb:hover,
.product-list-container::-webkit-scrollbar-thumb:hover,
.category-chips-wrapper::-webkit-scrollbar-thumb:hover,
.cart-content-area >>> .v-data-table__wrapper::-webkit-scrollbar-thumb:hover {
  background-color: #94a3b8;
}

.theme--dark .cart-content-area,
.theme--dark .product-list-container,
.theme--dark .category-chips-wrapper,
.theme--dark .cart-content-area >>> .v-data-table__wrapper {
  scrollbar-color: rgba(255, 255, 255, 0.2) transparent;
}

.theme--dark .cart-content-area::-webkit-scrollbar-thumb,
.theme--dark .product-list-container::-webkit-scrollbar-thumb,
.theme--dark .category-chips-wrapper::-webkit-scrollbar-thumb,
.theme--dark .cart-content-area >>> .v-data-table__wrapper::-webkit-scrollbar-thumb {
  background-color: rgba(255, 255, 255, 0.2);
}

.theme--dark .cart-content-area::-webkit-scrollbar-thumb:hover,
.theme--dark .product-list-container::-webkit-scrollbar-thumb:hover,
.theme--dark .category-chips-wrapper::-webkit-scrollbar-thumb:hover,
.theme--dark .cart-content-area >>> .v-data-table__wrapper::-webkit-scrollbar-thumb:hover {
  background-color: rgba(255, 255, 255, 0.35);
}

@media (max-width: 960px) {
  .purchase-page-wrapper {
    height: auto;
  }

  .purchase-page-container {
    flex: none;
  }

  .cart-content-area {
    flex: none;
    height: auto;
    max-height: 50vh;
  }

  .cart-summary-area {
    flex-shrink: 0;
    height: auto;
    margin-top: 16px;
  }

  .product-list-container {
    max-height: 400px;
  }

  .action-buttons {
    flex-direction: column;
    gap: 4px;
  }
}
</style>
